// Servicio de autenticación del lado del cliente.
// La configuración HTTP centralizada está en config/axiosConfig.js.
// Este módulo solo maneja: Firebase auth state, token refresh periódico,
// y llamadas al backend de auth usando la instancia compartida.

import { auth } from "../config/firebaseConfig";
import api from "../config/axiosConfig";

const TOKEN_REFRESH_INTERVAL = 15 * 60 * 1000;
const TOKEN_KEY = "firebase_auth_token";

const ROLES = {
    SUPER_ADMIN: 'super_admin',
    ADMINISTRADOR: 'administrador',
    AUDITOR_INTERIOR: 'auditor_interior',
    AUDITOR_CAPITAL: 'auditor_capital',
    AUDITOR_BSAS: 'auditor_bsas',
    AUDITOR_MEDICO_GENERAL: 'auditor_medico_general',
    OPERADOR: 'operador'
};

let refreshInterval = null;

const getStoredToken = () => localStorage.getItem(TOKEN_KEY);
const storeToken = (token) => { if (token) localStorage.setItem(TOKEN_KEY, token); };
const removeToken = () => localStorage.removeItem(TOKEN_KEY);

const refreshToken = async () => {
    try {
        const currentUser = auth.currentUser;
        if (currentUser) {
            const token = await currentUser.getIdToken(true);
            storeToken(token);
            return token;
        }
        removeToken();
        return null;
    } catch (error) {
        console.error("Error al refrescar token:", error);
        return null;
    }
};

const startTokenRefresh = () => {
    stopTokenRefresh();
    refreshToken();
    refreshInterval = setInterval(refreshToken, TOKEN_REFRESH_INTERVAL);
};

const stopTokenRefresh = () => {
    if (refreshInterval) {
        clearInterval(refreshInterval);
        refreshInterval = null;
    }
};

const initAuth = () => {
    auth.onAuthStateChanged((user) => {
        if (user) {
            startTokenRefresh();
        } else {
            stopTokenRefresh();
            removeToken();
        }
    });
};

const verifyToken = async () => {
    try {
        const token = await refreshToken();
        if (!token) return { valid: false, error: "No hay token almacenado" };
        const response = await api.get("/api/auth/verify-token");
        return { valid: true, user: response.data.user, refreshed: false };
    } catch (error) {
        if (error.response?.status === 401) {
            try {
                const newToken = await refreshToken();
                if (newToken) return { valid: true, refreshed: true };
            } catch (_) {}
        }
        return { valid: false, error: error.response?.data?.error || error.message };
    }
};

const verifyAdminClaims = async (uid) => {
    const response = await api.post("/api/auth/verify-admin-claims", { uid });
    return response.data;
};

const verifyUserClaims = async () => {
    const currentUser = auth.currentUser;
    if (!currentUser) return null;
    await refreshToken();
    const idTokenResult = await currentUser.getIdTokenResult(true);
    return {
        role: idTokenResult.claims.role || "usuario",
        permissions: idTokenResult.claims.permissions || [],
        isAdmin: idTokenResult.claims.admin === true ||
                idTokenResult.claims.role === "administrador" ||
                idTokenResult.claims.role === "super_admin",
        isSuperAdmin: idTokenResult.claims.role === "super_admin",
        isAuditor: [ROLES.AUDITOR_INTERIOR, ROLES.AUDITOR_CAPITAL, ROLES.AUDITOR_BSAS, ROLES.AUDITOR_MEDICO_GENERAL]
            .includes(idTokenResult.claims.role)
    };
};

const checkRole = async (requiredRole) => {
    const claims = await verifyUserClaims();
    if (!claims) return false;
    if (claims.isSuperAdmin) return true;
    if (Array.isArray(requiredRole)) return requiredRole.includes(claims.role);
    return claims.role === requiredRole;
};

const assignAdminRole = async (uid) => {
    const response = await api.post("/api/auth/assign-admin", { uid });
    return response.data;
};

const registerUser = async (userData) => {
    const response = await api.post("/api/auth/register", userData);
    return response.data;
};

const deleteUser = async (userId) => {
    const currentUser = auth.currentUser;
    if (!currentUser) throw new Error("No hay usuario autenticado");

    const idTokenResult = await currentUser.getIdTokenResult(true);
    const isAdmin = idTokenResult.claims.admin === true ||
                    idTokenResult.claims.role === "administrador";
    const isSuperAdmin = idTokenResult.claims.role === "super_admin";
    if (!isAdmin && !isSuperAdmin) throw new Error("No tienes permisos para eliminar usuarios");

    const maxRetries = 3;
    let retryCount = 0;
    let lastError = null;

    while (retryCount < maxRetries) {
        try {
            const token = await currentUser.getIdToken(true);
            if (!token) throw new Error("No se pudo obtener un token válido");
            storeToken(token);
            return await api.delete(`/api/auth/users/${userId}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
        } catch (error) {
            lastError = error;
            if (error.response?.status === 401) {
                const newToken = await refreshToken();
                if (!newToken) throw new Error("No se pudo renovar el token");
            } else {
                throw error;
            }
            retryCount++;
            await new Promise((r) => setTimeout(r, Math.pow(2, retryCount) * 1000));
        }
    }
    throw lastError || new Error("No se pudo completar la operación");
};

const updateUserClaims = async (uid, role, permissions) => {
    const response = await api.post("/api/auth/update-user-claims", { uid, role, permissions });
    return response.data;
};

export default {
    initAuth,
    refreshToken,
    getStoredToken,
    verifyToken,
    logout: async () => {
        try {
            stopTokenRefresh();
            removeToken();
            await auth.signOut();
            return true;
        } catch (error) {
            console.error("Error al cerrar sesión:", error);
            return false;
        }
    },
    startTokenRefresh,
    stopTokenRefresh,
    verifyAdminClaims,
    verifyUserClaims,
    assignAdminRole,
    registerUser,
    deleteUser,
    updateUserClaims,
    checkRole,
    ROLES
};
